import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Loader2, CheckCircle, Rocket, Users, Lightbulb,
  TrendingUp, Clock, Filter, RefreshCw, FileText,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UserManagement from '../components/UserManagement';
import CreationFunnelCard from '../components/admin/CreationFunnelCard';
import SsrCacheCard from '../components/admin/SsrCacheCard';
import AppleIapAuditCard from '../components/admin/AppleIapAuditCard';
import EmailHealthBanner from '../components/admin/EmailHealthBanner';
import IdeaRow from '../components/admin/IdeaRow';
import BulkOperations from '../components/admin/BulkOperations';
import ScraperHealthWidget from '../components/admin/ScraperHealthWidget';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_SECRET = 'amp-admin-2025-secret';
const ADMIN_EMAIL = 'oanjorin@anjolc.com';

const AdminPanel = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, loginWithGoogle } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [adminKey] = useState(ADMIN_SECRET);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showPendingOnly, setShowPendingOnly] = useState(false);
  const [massApproving, setMassApproving] = useState(false);
  const [pendingNonAdminIdeas, setPendingNonAdminIdeas] = useState([]);

  useEffect(() => {
    if (!authLoading) {
      if (isAuthenticated && user?.email === ADMIN_EMAIL) {
        setIsAuthorized(true);
      } else {
        setIsAuthorized(false);
      }
    }
  }, [authLoading, isAuthenticated, user]);

  useEffect(() => {
    if (isAuthorized) {
      fetchData();
      if (showPendingOnly) {
        fetchPendingNonAdmin();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorized, statusFilter, showPendingOnly]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ideasData, statsData] = await Promise.all([
        apiService.adminGetIdeas(adminKey, statusFilter === 'all' ? null : statusFilter),
        apiService.adminGetStats(adminKey),
      ]);
      setIdeas(ideasData.ideas || []);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      if (error.message.includes('403')) {
        setIsAuthorized(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingNonAdmin = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin-panel/ideas/pending-non-admin`, {
        headers: { 'X-Admin-Key': adminKey },
      });
      if (response.ok) {
        const data = await response.json();
        setPendingNonAdminIdeas(data.ideas || []);
      }
    } catch (error) {
      console.error('Failed to fetch pending non-admin ideas:', error);
    }
  };

  const handleSelectIdea = (ideaId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(ideaId);
      else next.delete(ideaId);
      return next;
    });
  };

  const handleSelectAllPending = () => {
    const ideasToSelect = showPendingOnly ? pendingNonAdminIdeas : ideas.filter((i) => i.status === 'pending');
    const validIds = ideasToSelect
      .filter((i) => i.has_category !== false && i.has_subcategory !== false)
      .map((i) => i.id);
    setSelectedIds(new Set(validIds));
  };

  const handleDeselectAll = () => setSelectedIds(new Set());

  const handleMassApprove = async () => {
    if (selectedIds.size === 0) return;
    setMassApproving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin-panel/ideas/mass-approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify({ idea_ids: Array.from(selectedIds) }),
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Approved: ${result.approved}, Failed: ${result.failed?.length || 0}, Errors: ${result.errors?.length || 0}`);
        setSelectedIds(new Set());
        fetchData();
        if (showPendingOnly) fetchPendingNonAdmin();
      }
    } catch (error) {
      console.error('Mass approve failed:', error);
    } finally {
      setMassApproving(false);
    }
  };

  const handleApprove = async (ideaId) => {
    try {
      await apiService.adminUpdateIdeaStatus(adminKey, ideaId, 'approved');
      setIdeas((prev) => prev.map((idea) => (idea.id === ideaId ? { ...idea, status: 'approved' } : idea)));
    } catch (error) {
      console.error('Failed to approve:', error);
    }
  };

  const handleReject = async (ideaId) => {
    try {
      await apiService.adminUpdateIdeaStatus(adminKey, ideaId, 'rejected');
      setIdeas((prev) => prev.map((idea) => (idea.id === ideaId ? { ...idea, status: 'rejected' } : idea)));
    } catch (error) {
      console.error('Failed to reject:', error);
    }
  };

  const handleEditIdea = async (ideaId, editData) => {
    const response = await fetch(`${API_URL}/api/admin-panel/ideas/${ideaId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
      body: JSON.stringify(editData),
    });

    if (response.ok) {
      const updated = await response.json();
      const patch = (idea) => (idea.id === ideaId
        ? { ...idea, ...updated, has_category: true, has_subcategory: !!updated.subcategory }
        : idea);
      setIdeas((prev) => prev.map(patch));
      setPendingNonAdminIdeas((prev) => prev.map(patch));
    } else {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update');
    }
  };

  const handleLoadToMarket = async (ideaId, closesAt, initialPrice) => {
    try {
      const result = await apiService.adminLoadToMarket(adminKey, ideaId, closesAt, initialPrice);
      setIdeas((prev) => prev.map((idea) => (idea.id === ideaId ? { ...idea, status: 'live', market_id: result.market_id } : idea)));
    } catch (error) {
      console.error('Failed to load to market:', error);
    }
  };

  const handleResolveMarket = async (marketId, winningOutcome, resolutionReason) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      const sessionToken = localStorage.getItem('session_token');
      if (sessionToken) headers.Authorization = `Bearer ${sessionToken}`;

      const res = await fetch(`${API_URL}/api/admin/markets/${marketId}/resolve`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify({ winning_outcome: winningOutcome, resolution_reason: resolutionReason }),
      });

      if (res.ok) {
        setIdeas((prev) => prev.map((idea) => (idea.market_id === marketId ? { ...idea, status: 'resolved' } : idea)));
        fetchData();
      } else {
        const error = await res.json();
        console.error('Failed to resolve market:', error.detail);
      }
    } catch (error) {
      console.error('Failed to resolve market:', error);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="flex items-center justify-center py-40">
          <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
          <span className="ml-3 text-gray-400">Loading...</span>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[#050810]">
        <Header />
        <div className="max-w-md mx-auto px-4 py-20">
          <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-8">
            <div className="text-center mb-6">
              <Shield className="h-12 w-12 text-red-500 mx-auto mb-3" />
              <h1 className="text-2xl font-bold text-white">Admin Access Only</h1>
              <p className="text-gray-400 mt-2">
                {!isAuthenticated
                  ? 'Please sign in with the admin Google account to access this panel.'
                  : "You don't have permission to access this panel. Admin access is restricted."}
              </p>
            </div>

            {!isAuthenticated && (
              <Button onClick={loginWithGoogle} className="w-full bg-[#3B82F6] hover:bg-[#2563EB] text-white">
                Sign in with Google
              </Button>
            )}

            {isAuthenticated && (
              <div className="text-center">
                <p className="text-gray-500 text-sm mb-4">Signed in as: {user?.email}</p>
                <Button onClick={() => navigate('/')} className="bg-[#1e2a45] hover:bg-[#2d3a56] text-white">
                  Go to Homepage
                </Button>
              </div>
            )}
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050810]">
      <Header />

      {/* Header Section */}
      <div className="bg-gradient-to-b from-[#131b2e] to-[#050810] border-b border-[#1e2a45]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-[#3B82F6]" />
              <div>
                <h1 className="text-2xl font-bold text-white">Admin Panel</h1>
                <p className="text-gray-400 text-sm">Manage market ideas and content</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/admin/refunds')}
                variant="outline"
                className="border-[#F97316]/50 text-[#F97316] hover:bg-[#F97316]/10"
                data-testid="admin-refunds-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Manage Refunds
              </Button>
              <Button
                onClick={() => navigate('/admin/press')}
                variant="outline"
                className="border-[#3B82F6]/50 text-[#3B82F6] hover:bg-[#3B82F6]/10"
                data-testid="admin-press-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Press Inquiries
              </Button>
              <Button
                onClick={() => navigate('/admin/resolution-audit')}
                variant="outline"
                className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                data-testid="admin-resolution-audit-btn"
              >
                <FileText className="h-4 w-4 mr-2" />
                Resolution Audit
              </Button>
              <Button
                onClick={() => navigate('/admin/sports')}
                variant="outline"
                className="border-[#3B82F6]/50 text-[#3B82F6] hover:bg-[#3B82F6]/10"
                data-testid="admin-sports-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Sports Feeds
              </Button>
              <Button
                onClick={fetchData}
                variant="outline"
                className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
                data-testid="refresh-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Stats */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="h-5 w-5 text-yellow-400" />
                  <span className="text-gray-400 text-sm">Pending</span>
                </div>
                <span className="text-2xl font-bold text-white">{stats.ideas?.pending || 0}</span>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-5 w-5 text-blue-400" />
                  <span className="text-gray-400 text-sm">Approved</span>
                </div>
                <span className="text-2xl font-bold text-white">{stats.ideas?.approved || 0}</span>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-green-400" />
                  <span className="text-gray-400 text-sm">Live Markets</span>
                </div>
                <span className="text-2xl font-bold text-white">{stats.ideas?.live || 0}</span>
              </div>
              <div className="bg-[#1e2a45]/50 rounded-xl p-4 border border-[#1e2a45]">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="h-5 w-5 text-purple-400" />
                  <span className="text-gray-400 text-sm">Total Users</span>
                </div>
                <span className="text-2xl font-bold text-white">{stats.users || 0}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Market Creation Funnel + SSR Cache (LLM crawlers) + Apple IAP Audit */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <EmailHealthBanner />
        <CreationFunnelCard />
        <SsrCacheCard />
        <AppleIapAuditCard />
      </div>

      {/* Bulk Operations Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <BulkOperations adminKey={adminKey} onRefresh={fetchData} />
      </div>

      {/* Scraper Health Widget */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <ScraperHealthWidget />
      </div>

      {/* User Management Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-6">
          <UserManagement />
        </div>
      </div>

      {/* Ideas List */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {/* Mass Approve Controls */}
        <div className="bg-[#131b2e] rounded-2xl border border-[#1e2a45] p-4 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-400" />
                Mass Approve
              </h2>
              <label className="flex items-center gap-2 text-sm text-gray-400">
                <input
                  type="checkbox"
                  checked={showPendingOnly}
                  onChange={(e) => setShowPendingOnly(e.target.checked)}
                  className="w-4 h-4 rounded border-[#1e2a45] bg-[#1e2a45] text-[#3B82F6] focus:ring-[#3B82F6]"
                />
                Show only pending (non-admin)
              </label>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={handleSelectAllPending}
                size="sm"
                variant="outline"
                className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
                data-testid="select-all-pending-btn"
              >
                Select All Valid
              </Button>
              <Button
                onClick={handleDeselectAll}
                size="sm"
                variant="outline"
                className="border-[#1e2a45] text-gray-300 hover:bg-[#1e2a45]"
              >
                Deselect All
              </Button>
              <Button
                onClick={handleMassApprove}
                disabled={selectedIds.size === 0 || massApproving}
                size="sm"
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="mass-approve-btn"
              >
                {massApproving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle className="h-4 w-4 mr-1" />}
                Approve All Selected ({selectedIds.size})
              </Button>
            </div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Only ideas with both category and subcategory can be approved. Ideas from admin users are excluded.
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-400">Status:</span>
          </div>
          <div className="flex gap-2">
            {['all', 'pending', 'approved', 'rejected', 'live'].map((status) => (
              <button
                key={status}
                onClick={() => setStatusFilter(status)}
                data-testid={`filter-${status}`}
                className={`px-3 py-1.5 text-sm font-medium rounded-full transition-all ${
                  statusFilter === status
                    ? 'bg-[#3B82F6] text-white'
                    : 'bg-[#1e2a45] text-gray-400 hover:bg-[#2d3a56]'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 text-[#3B82F6] animate-spin" />
            <span className="ml-3 text-gray-400">Loading ideas...</span>
          </div>
        ) : (showPendingOnly ? pendingNonAdminIdeas : ideas).length === 0 ? (
          <div className="text-center py-20">
            <Lightbulb className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No ideas found</h3>
            <p className="text-gray-400">No ideas match the current filter.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {(showPendingOnly ? pendingNonAdminIdeas : ideas).map((idea) => (
              <IdeaRow
                key={idea.id}
                idea={idea}
                expanded={expandedId === idea.id}
                onToggle={() => setExpandedId(expandedId === idea.id ? null : idea.id)}
                onApprove={handleApprove}
                onReject={handleReject}
                onLoadToMarket={handleLoadToMarket}
                onEditIdea={handleEditIdea}
                onResolveMarket={handleResolveMarket}
                selected={selectedIds.has(idea.id)}
                onSelect={handleSelectIdea}
                showCheckbox={idea.status === 'pending'}
              />
            ))}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default AdminPanel;
